import { motion, useSpring, useTransform } from 'framer-motion'
import { useEffect } from 'react'
import { useCoordinationMetrics } from '../hooks/useCoordinationMetrics'

// Get score color
function getScoreColor(score: number): string {
  if (score >= 80) return '#10b981' // emerald-500
  if (score >= 60) return '#f59e0b' // amber-500
  if (score >= 40) return '#f97316' // orange-500
  return '#ef4444' // red-500
}

// Get score glow color
function getScoreGlowColor(score: number): string {
  if (score >= 80) return 'rgba(16, 185, 129, 0.5)'
  if (score >= 60) return 'rgba(245, 158, 11, 0.5)'
  if (score >= 40) return 'rgba(249, 115, 22, 0.5)'
  return 'rgba(239, 68, 68, 0.5)'
}

// Get score label
function getScoreLabel(score: number): string {
  if (score >= 80) return 'Excellent'
  if (score >= 60) return 'Good'
  if (score >= 40) return 'Fair'
  return 'Poor'
}

// Animated number component with counting effect
function AnimatedScore({ value }: { value: number }) {
  const spring = useSpring(0, { stiffness: 50, damping: 15 })
  const display = useTransform(spring, (current) => Math.round(current))

  useEffect(() => {
    spring.set(value)
  }, [value, spring])

  return (
    <motion.span>
      {display}
    </motion.span>
  )
}

export default function CoordinationScore() {
  const { score, metrics, loading, error } = useCoordinationMetrics()
  const color = getScoreColor(score)
  const glowColor = getScoreGlowColor(score)
  const label = getScoreLabel(score)

  if (loading) {
    return (
      <motion.div 
        className="glass-panel rounded-xl p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-center h-24">
          <motion.div
            className="w-10 h-10 border-3 border-gray-700 border-t-cyan-500 rounded-full"
            style={{
              border: '3px solid rgba(55, 65, 81, 0.3)',
              borderTopColor: '#00d4ff',
              boxShadow: '0 0 20px rgba(0, 212, 255, 0.2)',
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      </motion.div>
    )
  }

  if (error) {
    return (
      <motion.div 
        className="glass-panel rounded-xl p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-center h-24 text-red-400">
          <span className="text-sm">Failed to load score</span>
        </div>
      </motion.div>
    )
  }

  const totalActions = metrics.actionOutcomes.reduce((sum, o) => sum + o.action_count, 0)
  const avgSuccessRate = metrics.actionOutcomes.length > 0
    ? metrics.actionOutcomes.reduce((sum, o) => sum + (o.success_rate || 0), 0) / metrics.actionOutcomes.length
    : 0

  return (
    <motion.div 
      className="glass-panel rounded-xl p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      whileHover={{ 
        boxShadow: `0 12px 40px rgba(0, 0, 0, 0.4), 0 0 30px ${glowColor}40`,
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Team Coordination</h3>
        <motion.div
          className="w-3 h-3 rounded-full"
          style={{ 
            backgroundColor: color,
            boxShadow: `0 0 10px ${glowColor}`,
          }}
          animate={{ 
            boxShadow: [
              `0 0 10px ${glowColor}60`,
              `0 0 20px ${glowColor}90`,
              `0 0 10px ${glowColor}60`
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </div>

      {/* Score display */}
      <div className="flex items-center gap-6">
        {/* Circular score with enhanced glow */}
        <div className="relative">
          <motion.svg 
            className="w-24 h-24 -rotate-90"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* Background circle with subtle glow */}
            <circle
              cx="48"
              cy="48"
              r="40"
              fill="none"
              stroke="#1f2937"
              strokeWidth="8"
              style={{
                filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.5))',
              }}
            />
            {/* Progress circle with animated glow */}
            <motion.circle
              cx="48"
              cy="48"
              r="40"
              fill="none"
              stroke={color}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 40}`}
              initial={{ strokeDashoffset: `${2 * Math.PI * 40}` }}
              animate={{ strokeDashoffset: `${2 * Math.PI * 40 * (1 - score / 100)}` }}
              transition={{ duration: 1.5, ease: 'easeOut', delay: 0.3 }}
              style={{
                filter: `drop-shadow(0 0 8px ${glowColor})`,
              }}
            />
          </motion.svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.span 
              className="text-3xl font-bold"
              style={{ 
                color,
                textShadow: `0 0 20px ${glowColor}`,
              }}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <AnimatedScore value={score} />
            </motion.span>
          </div>
        </div>

        {/* Score details */}
        <div className="flex-1">
          <motion.div 
            className="text-sm font-medium mb-1"
            style={{ color }}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            {label}
          </motion.div>
          <motion.div 
            className="text-xs text-gray-500 mb-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.6 }}
          >
            Based on success rate, activity, and collaboration
          </motion.div>
          
          {/* Mini stats with glass effect */}
          <div className="grid grid-cols-2 gap-2">
            <motion.div 
              className="glass-card rounded-lg p-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.7 }}
              whileHover={{ scale: 1.02 }}
            >
              <motion.div 
                className="text-lg font-semibold text-white"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                {Math.round(avgSuccessRate)}%
              </motion.div>
              <div className="text-xs text-gray-500">Success rate</div>
            </motion.div>
            <motion.div 
              className="glass-card rounded-lg p-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.8 }}
              whileHover={{ scale: 1.02 }}
            >
              <motion.div 
                className="text-lg font-semibold text-white"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                {totalActions}
              </motion.div>
              <div className="text-xs text-gray-500">Actions</div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Agent breakdown */}
      {metrics.actionOutcomes.length > 0 && (
        <motion.div 
          className="mt-4 pt-4 border-t border-gray-700/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 1 }}
        >
          <div className="text-xs text-gray-500 mb-2">Agent Performance</div>
          <div className="space-y-2">
            {metrics.actionOutcomes.map((outcome, index) => (
              <motion.div 
                key={outcome.agent} 
                className="flex items-center gap-2 group cursor-default"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 1.1 + index * 0.1 }}
              >
                <span className="text-xs text-gray-400 w-16">{outcome.agent}</span>
                <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ 
                      backgroundColor: getScoreColor(outcome.success_rate || 0),
                      boxShadow: `0 0 10px ${getScoreGlowColor(outcome.success_rate || 0)}`,
                    }}
                    initial={{ width: 0 }}
                    animate={{ width: `${outcome.success_rate || 0}%` }}
                    transition={{ duration: 1, delay: 1.2 + index * 0.1 }}
                  />
                </div>
                <motion.span 
                  className="text-xs text-gray-500 w-10 text-right group-hover:text-white transition-colors"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.3 + index * 0.1 }}
                >
                  {Math.round(outcome.success_rate || 0)}%
                </motion.span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Collaboration indicator */}
      {metrics.collaborationMetrics.length > 0 && (
        <motion.div 
          className="mt-4 pt-4 border-t border-gray-700/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 1.4 }}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Cross-agent collaborations</span>
            <motion.span 
              className="text-sm font-medium"
              style={{ color: '#00d4ff' }}
              animate={{
                textShadow: [
                  '0 0 10px rgba(0, 212, 255, 0.3)',
                  '0 0 20px rgba(0, 212, 255, 0.6)',
                  '0 0 10px rgba(0, 212, 255, 0.3)',
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {metrics.collaborationMetrics.reduce((sum, c) => sum + c.collaboration_count, 0)}
            </motion.span>
          </div>
        </motion.div>
      )}
    </motion.div>
  )
}
